/**
 * Назначить будущий приход на платёж.
 *
 * Отдельный экран, а не поле в конверте, потому что действие другое. Конверт
 * закрепляет деньги, которые лежат на счёте; здесь человек распоряжается
 * деньгами, которых ещё нет. Приложение обязано держать это врозь до самого
 * конца: пока зарплата не пришла, платёж не обеспечен, чем бы он ни был
 * назначен.
 *
 * Два запрета живут здесь же и видны на экране:
 *
 * · приход, который приходит после срока, не покрывает платёж — и об этом
 *   говорится до нажатия, а не после;
 * · один приход нельзя обещать дважды — свободный остаток показан числом.
 */
import { useState } from 'react';
import {
  ZERO, clampPos, expectedIncomes, formatMoney, freeFromIncome, isAfter, isPlanned, minM, sourceOf, sub,
  type Money, type PlainDate,
  futureMoneyKey, type FutureMoney, type PlannedAllocation,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { SheetWrap } from './sheet.js';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import { formatAmountInput, parseAmountInput, planFromIncome, receivedIncomes, type AppState } from './store.js';

export interface PlanTarget {
  /** Наступление платежа: `${obligationId}:${scheduledDate}`; у кредитки — `card:${accountId}`. */
  instanceId: string;
  obligationId: string;
  /** Кредитка: назначение — на её платёж к `dueDate`, на приходе станет её конвертом. */
  cardId?: string;
  label: string;
  dueDate: PlainDate;
  /** Сколько ещё не покрыто ничем. */
  need: Money;
  /**
   * «Отложить на декабрь» (04.11.2026): приход по умолчанию — самый крупный
   * свободный в этом отрезке, а не первый по дате.
   */
  window?: { from: PlainDate; to: PlainDate };
  /**
   * «Отложить на декабрь»: сколько в том отрезке не хватит и после
   * отложенного. Тогда строки говорят «Отложить», а не «Не хватает» и не
   * «Нехватка закрыта» — закрывается только отложенное, не весь декабрь.
   */
  stillShort?: { month: string; gap: Money };
}

/** На что назначены деньги — словами, для списков назначений. */
export function allocationLabel(state: AppState, target: PlannedAllocation['target']): string {
  if (target.kind === 'goal') return state.goals.find((g) => g.id === target.goalId)?.name ?? 'Цель';
  // С датой: на одну карту бывают назначения и на ближайший, и на следующий платёж (04.11.2026).
  if (target.kind === 'card') return `${state.accounts.find((a) => a.id === target.accountId)?.name ?? 'Кредитка'}, ${formatDate(target.dueDate)}`;
  return state.obligations.find((o) => o.id === target.obligationId)?.name ?? 'Платёж';
}

/**
 * Назначенное из будущих приходов на каждый ближайший платёж — то, что
 * приходит к сроку (04.11.2026). «Заметил» вычитает его из нехватки, лист
 * нехватки показывает с «Отменить».
 */
export function plannedFor(state: AppState, u: { kind: 'obligation' | 'credit'; id: string; date: PlainDate }): PlannedAllocation[] {
  return (state.plannedAllocations ?? []).filter((a) => isPlanned(a) && !isAfter(a.expectedDate, u.date)
    && (u.kind === 'credit'
      ? a.target.kind === 'card' && a.target.accountId === u.id && a.target.dueDate === u.date
      : a.target.kind === 'obligation' && a.target.instanceId === `${u.id}:${u.date}`));
}

/** Чей приход: имя дохода или ожидания. */
export function plannedSource(state: AppState, a: PlannedAllocation): string {
  const src = sourceOf(a);
  return src.kind === 'stream_occurrence'
    ? state.incomeStreams.find((x) => x.id === src.incomeStreamId)?.name ?? 'приход'
    : state.expectedIncomes.find((x) => x.id === src.expectationId)?.name ?? 'приход';
}

/**
 * «Назначено из прихода» с «Отменить» (04.11.2026): владелец назначил
 * зарплату на кредитку и не нашёл, где это снять.
 */
export function PlannedRows({ state, list, onCancel }: {
  state: AppState; list: readonly PlannedAllocation[]; onCancel: (id: string) => void;
}) {
  if (list.length === 0) return null;
  return (
    <section className="blk">
      <div className="blkhead"><b>Назначено из прихода</b></div>
      {list.map((a) => (
        <div className="li" key={a.id}>
          <div className="nm"><b>{plannedSource(state, a)} · {formatDate(a.expectedDate)}</b>
            <span>деньги закрепятся, когда приход придёт</span></div>
          <div className="am num">{formatMoney(a.amount)}</div>
          <button type="button" className="textact inline" onClick={() => onCancel(a.id)}>Отменить</button>
        </div>
      ))}
    </section>
  );
}

const MONTHS = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
const formatDate = (d: string): string => {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}`;
};

export function PlanFromIncome({ state, target, onClose, onSaved }: {
  state: AppState;
  target: PlanTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Уже пришедшее не ожидается второй раз: назначать можно только из того,
  // чего ещё нет.
  const incomes = expectedIncomes({
    streams: state.incomeStreams, today: state.today,
    received: receivedIncomes(state.transactions),
    expectations: state.expectedIncomes,
  });

  /**
   * Ключ прихода для выбора: у наступления дохода это источник и дата, у
   * ожидания — его собственный ключ. Раньше назначать можно было только от
   * регулярного дохода, и гонорар, которым платёж обычно и закрывают, в
   * список не попадал.
   */
  const sourceOfRow = (row: typeof incomes[number]): FutureMoney => (row.expectationId
    ? { kind: 'expectation', expectationId: row.expectationId }
    : {
      kind: 'stream_occurrence',
      incomeStreamId: row.incomeStreamId!,
      expectedDate: row.expectedDate,
    });

  const preferred = target.window
    ? [...incomes].filter((i) => !isAfter(target.window!.from, i.expectedDate) && !isAfter(i.expectedDate, target.window!.to))
      .sort((a, b) => freeFromIncome(state.plannedAllocations, { source: sourceOfRow(b), amount: b.amount })
        - freeFromIncome(state.plannedAllocations, { source: sourceOfRow(a), amount: a.amount }))[0]
    : undefined;
  const [pickedId, setPicked] = useState(
    (preferred ?? incomes[0]) ? futureMoneyKey(sourceOfRow((preferred ?? incomes[0])!)) : '',
  );
  const picked = incomes.find((i) => futureMoneyKey(sourceOfRow(i)) === pickedId) ?? incomes[0];
  const free = picked
    ? freeFromIncome(state.plannedAllocations, {
      source: sourceOfRow(picked), amount: picked.amount,
    })
    : ZERO;
  const [raw, setRaw] = useState(() => '');
  const amount = raw.trim() === '' ? minM(target.need, free) : parseAmountInput(raw);
  const [busy, setBusy] = useState(false);

  if (!picked) {
    return (
      <SheetWrap title="Назначить из прихода" onClose={onClose}>
        <div className="form">
          <div className="note">
            Ожидаемых приходов нет — назначать нечего. Добавь доход, и тогда
            его можно будет распределить заранее.
          </div>
        </div>
      </SheetWrap>
    );
  }

  // Поздние деньги не покрывают ранний срок. Это не придирка: платёж 9-го,
  // на который назначена зарплата 10-го, останется неоплаченным, а человек
  // будет считать вопрос решённым.
  const late = isAfter(picked.expectedDate, target.dueDate);
  const tooMuch = amount > free;
  /*
    Что останется после назначения (03.11.2026). Прежде лист этого не
    говорил, и было не понять, что ROWI на 44 621 ₽ нехватку в 55 950 ₽ не
    закроет.
  */
  const covers = late || tooMuch ? ZERO : amount;
  const left = clampPos(sub(target.need, covers));
  // Кому обещано остальное из этого прихода — по именам: «другим платежам»
  // ничего не объясняло, особенно когда других назначений нет вовсе.
  const pickedKey = futureMoneyKey(sourceOfRow(picked));
  const promisedTo = state.plannedAllocations
    .filter((x) => isPlanned(x) && futureMoneyKey(sourceOf(x)) === pickedKey)
    .map((x) => `«${allocationLabel(state, x.target)}» ${formatMoney(x.amount)}`);

  return (
    <SheetWrap title="Назначить из прихода" onClose={onClose}>
      {/* Что закрываем — строками, как в листе нехватки. */}
      <section className="blk">
        <div className="row"><div className="rl">{target.label}
          <span className="rn">оплатить до {formatDate(target.dueDate)}</span></div></div>
        <div className="row"><div className="rl">{target.stillShort ? 'Отложить' : 'Не хватает'}</div>
          <div className="rv num">{formatMoney(target.need)}</div></div>
      </section>

      {/* Приходы — выбором, как часть плана: сразу видно, сколько в каждом свободно и успевает ли он. */}
      <p className="choice-lead q pi-q">Из какого прихода?</p>
      <div className="fieldlist choice-list choice-icons">
        {incomes.map((i) => {
          const key = futureMoneyKey(sourceOfRow(i));
          const rowFree = freeFromIncome(state.plannedAllocations, { source: sourceOfRow(i), amount: i.amount });
          const rowLate = isAfter(i.expectedDate, target.dueDate);
          return (
            <button key={key} type="button" className={`li w${key === pickedId ? ' on' : ''}`} aria-pressed={key === pickedId}
              style={{ ['--cat' as string]: rowLate ? 'var(--warn)' : 'var(--ok)' }}
              onClick={() => { setPicked(key); setRaw(''); }}>
              <span className="catico choice-ico"><Icon name="calendar-check" size={18} /></span>
              <span className="nm"><em className="choice-answer">{i.name} · {formatDate(i.expectedDate)}</em>
                <span>{rowLate ? 'придёт позже срока' : `свободно ${formatMoney(rowFree)}`}</span>
                {rowFree < i.amount && <small className="choice-sub">из {formatMoney(i.amount)}</small>}</span>
              <span className={`ops-radio${key === pickedId ? ' on' : ''}`} aria-hidden="true">{key === pickedId && <Icon name="check" size={15} />}</span>
            </button>
          );
        })}
      </div>

      <div className="form pi-form">
        <label>Сколько назначить
          <Tip>Деньги остаются будущими: конверта не появится, «Можно тратить» не изменится.
            Когда приход случится, приложение предложит закрепить назначенное — и сделает это
            только с подтверждения.</Tip>
          <AmountInput value={raw} onChange={setRaw}
            placeholder={formatAmountInput(String(minM(target.need, free) / 100))}
            label="Сколько назначить" />
        </label>
      </div>

      {late ? (
        <div className="warnline">
          {picked.name} придёт {formatDate(picked.expectedDate)} — позже срока {formatDate(target.dueDate)}.
          К сроку этих денег не будет, и не хватать будет по-прежнему {formatMoney(target.need)}.
        </div>
      ) : tooMuch ? (
        <div className="warnline bad">
          {promisedTo.length > 0
            ? <>Из {picked.name} свободно {formatMoney(free)} из {formatMoney(picked.amount)}: остальное уже назначено — {promisedTo.join(', ')}.</>
            : <>{picked.name} — это {formatMoney(picked.amount)}. Больше, чем придёт, назначить нельзя.</>}
        </div>
      ) : (
        <section className="blk pi-after">
          {target.stillShort ? (
            <div className="row"><div className="rl">В {target.stillShort.month} всё равно не хватит
              <span className="rn">меньше тратить — или отложить больше из другого прихода</span></div>
              <div className="rv num warn">≈ {formatMoney(clampPos(sub(target.stillShort.gap, covers)))}</div></div>
          ) : (
          <div className="row"><div className="rl">{left > ZERO ? 'После назначения не хватит' : 'Нехватка закрыта'}
            {left > ZERO && <span className="rn">{picked.name} закроет {formatMoney(covers)} из {formatMoney(target.need)}</span>}</div>
            <div className={`rv num${left > ZERO ? ' warn' : ' ok'}`}>{left > ZERO ? formatMoney(left) : <Icon name="check" size={15} />}</div></div>
          )}
        </section>
      )}

      <button className="btn pri" disabled={busy || tooMuch || amount <= ZERO}
        onClick={async () => {
          setBusy(true);
          const to: PlannedAllocation['target'] = target.cardId
            ? { kind: 'card', accountId: target.cardId, dueDate: target.dueDate }
            : {
              kind: 'obligation',
              instanceId: target.instanceId,
              obligationId: target.obligationId,
            };
          await planFromIncome({
            source: sourceOfRow(picked),
            expectedDate: picked.expectedDate,
            amount,
            expected: picked.amount,
            target: to,
          });
          // Подтверждение после действия (04.11.2026): владелец не понимал, что произошло.
          flash(`Назначено ${formatMoney(amount)} из «${picked.name}» ${formatDate(picked.expectedDate)} на «${allocationLabel(state, to)}»`);
          onSaved();
        }}>
        {late ? 'Назначить, платёж останется необеспеченным' : `Назначить ${formatMoney(amount > ZERO ? amount : ZERO)}`}
      </button>
    </SheetWrap>
  );
}
